/**
 * SortableMasonry — the inspiration board's picture wall, with drag-to-rearrange
 * (docs/INSPIRATION_BOARD.md, "Arranging pictures").
 *
 * Why not CSS columns any more: columns flow top-to-bottom, so "the next picture" jumps between
 * columns unpredictably and nothing can animate between places. Here every card is absolutely
 * positioned with a transform — Pinterest's rule, each card into the currently shortest column, in
 * list order — so moving a card in the list slides every affected card to its new place.
 *
 * Interaction (pointer events, not HTML5 drag-and-drop, so the card itself can be animated):
 *   - mouse: press and move 5px to LIFT; touch: press and hold ~0.3s (a quick swipe still scrolls).
 *   - lifted: the card scales up, tilts slightly and casts a deep shadow, following the pointer;
 *     a soft outline marks where it will land; neighbours slide out of the way as it passes over
 *     them; the board scrolls when held near its top or bottom edge.
 *   - drop: the card glides into its slot and settles back to size. Dropped over an element with
 *     data-drop-key (the board tabs) it goes to that board instead. Escape cancels.
 *   - a press without a lift is a click (opens the picture). Buttons and links inside a card never
 *     start a drag.
 *
 * Props: items (in order), renderItem(item), onOpen(id), onReorder(id, prevId, nextId),
 * onDropTarget(id, key), onHoverTarget(key|null), scrollRef (the scrolling element).
 */

import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'

const EASE = 'cubic-bezier(.2,.7,.2,1)'
const MOVE_MS = 320
const DROP_MS = 280

export default function SortableMasonry({ items, renderItem, onOpen, onReorder, onDropTarget, onHoverTarget, scrollRef }) {
  const wrapRef = useRef(null)
  const [width, setWidth] = useState(0)
  const [heights, setHeights] = useState({})
  const [order, setOrder] = useState(null)       // ids while a drag is in progress
  const [drag, setDrag] = useState(null)         // { id, phase: 'lift'|'drop', lx, ly }
  // Cards slide between places only while someone is arranging (from pick-up until shortly after
  // the drop). Everything else — pictures loading and reporting their real heights, a new upload,
  // a window resize — lands in place without animation, so the board never flies around on load.
  const [animating, setAnimating] = useState(false)
  const animTimer = useRef(0)
  const els = useRef(new Map())
  const live = useRef({})                        // latest layout + state for window listeners

  // Container width.
  useLayoutEffect(() => {
    const el = wrapRef.current
    if (!el) return
    const ro = new ResizeObserver(() => setWidth(el.clientWidth))
    ro.observe(el); setWidth(el.clientWidth)
    return () => ro.disconnect()
  }, [])

  // Card heights (pictures finish loading at their own pace, so heights change after first paint).
  const roRef = useRef(null)
  if (!roRef.current && typeof ResizeObserver !== 'undefined') {
    roRef.current = new ResizeObserver(entries => {
      setHeights(prev => {
        let next = null
        for (const en of entries) {
          const id = en.target.getAttribute('data-sort-id')
          const h = en.target.offsetHeight
          if (id && prev[id] !== h) { next = next || { ...prev }; next[id] = h }
        }
        return next || prev
      })
    })
  }
  useEffect(() => () => roRef.current && roRef.current.disconnect(), [])
  function cardRef(id) {
    return el => {
      const old = els.current.get(id)
      if (old && old !== el && roRef.current) roRef.current.unobserve(old)
      if (el) { els.current.set(id, el); roRef.current && roRef.current.observe(el) }
      else els.current.delete(id)
    }
  }

  const byId = useMemo(() => Object.fromEntries(items.map(i => [i.id, i])), [items])
  const ids = useMemo(() => {
    const base = order || items.map(i => i.id)
    return base.filter(id => byId[id])
  }, [order, items, byId])

  // Layout.
  const narrow = width > 0 && width < 700
  const gap = narrow ? 10 : 18
  const colMin = narrow ? 150 : 236
  const cols = Math.max(1, Math.floor((width + gap) / (colMin + gap)))
  const colW = width ? (width - gap * (cols - 1)) / cols : colMin
  const layout = useMemo(() => {
    const colH = new Array(cols).fill(0)
    const pos = {}
    for (const id of ids) {
      const it = byId[id]
      const h = heights[id] || (it.width && it.height ? colW * it.height / it.width + 56 : colW * 1.2)
      let c = 0
      for (let k = 1; k < cols; k++) if (colH[k] < colH[c] - 1) c = k
      pos[id] = { x: c * (colW + gap), y: colH[c], w: colW, h }
      colH[c] += h + gap
    }
    return { pos, height: Math.max(0, ...colH) - (ids.length ? gap : 0) }
  }, [ids, byId, heights, cols, colW, gap])

  live.current = { layout, ids, drag, order, items }


  /* ── Pointer handling ─────────────────────────────────────────────────────
     Window listeners must be the SAME function objects when added and removed, but the logic needs
     this render's props. So the logic lives on H.current (reassigned every render) and the window
     only ever sees the stable wrappers in `on`. */

  const gesture = useRef(null)
  const H = useRef({})
  const on = useMemo(() => ({
    move: e => H.current.move(e),
    up: e => H.current.up(e),
    cancel: () => H.current.end(false, true),
    touch: e => { if (gesture.current && gesture.current.lifted) e.preventDefault() },
    key: e => H.current.key(e),
  }), [])

  H.current.down = (e, id) => {
    if (e.button !== 0 || gesture.current) return
    if (e.target.closest('button, a, input, textarea, select, [data-no-drag]')) return
    const el = els.current.get(id)
    if (!el) return
    const rect = el.getBoundingClientRect()
    const g = {
      id, pointerId: e.pointerId, touch: e.pointerType === 'touch',
      x0: e.clientX, y0: e.clientY, cx: e.clientX, cy: e.clientY,
      ox: e.clientX - rect.left, oy: e.clientY - rect.top,
      lifted: false, original: null, lastSwap: 0, lastSwapAt: { x: 0, y: 0 }, target: null, holdTimer: null, raf: 0,
    }
    gesture.current = g
    if (g.touch) g.holdTimer = setTimeout(() => H.current.lift(), 320)
    window.addEventListener('pointermove', on.move, { passive: false })
    window.addEventListener('pointerup', on.up)
    window.addEventListener('pointercancel', on.cancel)
    window.addEventListener('touchmove', on.touch, { passive: false })
    window.addEventListener('keydown', on.key, true)
  }

  H.current.lift = () => {
    const g = gesture.current
    if (!g || g.lifted) return
    g.lifted = true
    g.original = live.current.ids.slice()
    clearTimeout(animTimer.current); setAnimating(true)
    setOrder(g.original.slice())
    H.current.place()
    if (g.touch && navigator.vibrate) { try { navigator.vibrate(12) } catch {} }
    document.body.classList.add('ib-dragging')
    H.current.scrollTick()
  }

  H.current.place = () => {
    const g = gesture.current
    const wrap = wrapRef.current
    if (!g || !wrap) return
    const r = wrap.getBoundingClientRect()
    setDrag({ id: g.id, phase: 'lift', lx: g.cx - r.left - g.ox, ly: g.cy - r.top - g.oy, wx: r.left, wy: r.top })
  }

  H.current.move = e => {
    const g = gesture.current
    if (!g || e.pointerId !== g.pointerId) return
    g.cx = e.clientX; g.cy = e.clientY
    const dist = Math.hypot(e.clientX - g.x0, e.clientY - g.y0)
    if (!g.lifted) {
      if (g.touch) { if (dist > 8) H.current.end(false) }   // a swipe: let the page scroll
      else if (dist > 5) H.current.lift()
      return
    }
    e.preventDefault()
    H.current.place()
    H.current.hit()
  }

  H.current.hit = () => {
    const g = gesture.current
    const wrap = wrapRef.current
    if (!g || !wrap) return
    // Over a drop target (a board tab)? The lifted card ignores the pointer, so this sees beneath it.
    const under = document.elementFromPoint(g.cx, g.cy)
    const tgt = under && under.closest('[data-drop-key]')
    const key = tgt ? tgt.getAttribute('data-drop-key') : null
    if (key !== g.target) { g.target = key; onHoverTarget && onHoverTarget(key) }
    if (key) return
    // Over another card? Take its place. A short calm-down after each swap keeps a card that moved
    // under the pointer (because the columns rebalanced) from swapping straight back.
    const r = wrap.getBoundingClientRect()
    const px = g.cx - r.left, py = g.cy - r.top
    const now = performance.now()
    if (now - g.lastSwap < 140 && Math.hypot(px - g.lastSwapAt.x, py - g.lastSwapAt.y) < 24) return
    const { layout, ids } = live.current
    let over = null
    for (const id of ids) {
      if (id === g.id) continue
      const p = layout.pos[id]
      if (p && px >= p.x && px <= p.x + p.w && py >= p.y && py <= p.y + p.h) { over = id; break }
    }
    if (!over) return
    const from = ids.indexOf(g.id), to = ids.indexOf(over)
    if (from < 0 || to < 0 || from === to) return
    const next = ids.slice()
    next.splice(from, 1); next.splice(to, 0, g.id)
    g.lastSwap = now; g.lastSwapAt = { x: px, y: py }
    setOrder(next)
  }

  // Held near the top or bottom of the board: scroll, faster the closer to the edge.
  H.current.scrollTick = () => {
    const g = gesture.current
    if (!g || !g.lifted) return
    const sc = scrollRef && scrollRef.current
    if (sc) {
      const r = sc.getBoundingClientRect()
      const edge = 80
      let dy = 0
      if (g.cy < r.top + edge) dy = -Math.ceil((r.top + edge - g.cy) / 6)
      else if (g.cy > r.bottom - edge) dy = Math.ceil((g.cy - (r.bottom - edge)) / 6)
      if (dy) { sc.scrollTop += dy; H.current.place(); H.current.hit() }
    }
    g.raf = requestAnimationFrame(() => H.current.scrollTick())
  }

  H.current.up = e => {
    const g = gesture.current
    if (!g || e.pointerId !== g.pointerId) return
    if (!g.lifted) { const id = g.id; H.current.end(false); onOpen && onOpen(id); return }
    H.current.end(true)
  }

  H.current.key = e => {
    if (e.key === 'Escape' && gesture.current && gesture.current.lifted) { e.stopPropagation(); e.preventDefault(); H.current.end(false, true) }
  }

  H.current.end = (commit, cancelled) => {
    const g = gesture.current
    gesture.current = null
    window.removeEventListener('pointermove', on.move, { passive: false })
    window.removeEventListener('pointerup', on.up)
    window.removeEventListener('pointercancel', on.cancel)
    window.removeEventListener('touchmove', on.touch, { passive: false })
    window.removeEventListener('keydown', on.key, true)
    if (!g) return
    clearTimeout(g.holdTimer)
    cancelAnimationFrame(g.raf)
    document.body.classList.remove('ib-dragging')
    if (!g.lifted) return
    onHoverTarget && onHoverTarget(null)
    const finalOrder = live.current.order || g.original
    const target = commit && !cancelled ? g.target : null
    if (cancelled || target) setOrder(g.original)
    // Glide into the slot, then hand control back to the normal layout.
    const wr = wrapRef.current ? wrapRef.current.getBoundingClientRect() : { left: 0, top: 0 }
    setDrag({ id: g.id, phase: 'drop', wx: wr.left, wy: wr.top })
    setTimeout(() => { setDrag(null); setOrder(null) }, DROP_MS + 40)
    animTimer.current = setTimeout(() => setAnimating(false), DROP_MS + 700)
    if (target) { onDropTarget && onDropTarget(g.id, target); return }
    if (!commit || cancelled) return
    if (g.original.join('|') === finalOrder.join('|')) return
    const i = finalOrder.indexOf(g.id)
    onReorder && onReorder(g.id, finalOrder[i - 1] || null, finalOrder[i + 1] || null, finalOrder)
  }

  useEffect(() => () => { clearTimeout(animTimer.current); if (gesture.current) H.current.end(false, true) }, [])

  /* ── Render ───────────────────────────────────────────────────────────────── */

  const dragPos = drag && layout.pos[drag.id]
  return (
    <div ref={wrapRef} className="ib-sort" style={{ position: 'relative', height: layout.height, minHeight: 40 }}>
      {drag && drag.phase === 'lift' && dragPos && (
        <div className="ib-sort-slot" style={{ width: dragPos.w, height: dragPos.h, transform: `translate3d(${dragPos.x}px, ${dragPos.y}px, 0)` }} />
      )}
      {ids.map(id => {
        const p = layout.pos[id]
        if (!p) return null
        const isDrag = drag && drag.id === id
        const lifted = isDrag && drag.phase === 'lift'
        // The held card (and its glide home) is fixed-positioned at the wall's on-screen origin, so it
        // floats over the board header instead of being clipped by the scrolling area it lives in.
        // Transforms stay wall-relative, so switching back to absolute at the end never jumps.
        const floating = isDrag && drag.wx != null
        const style = {
          position: floating ? 'fixed' : 'absolute', left: floating ? drag.wx : 0, top: floating ? drag.wy : 0, width: p.w,
          transform: lifted ? `translate3d(${drag.lx}px, ${drag.ly}px, 0)` : `translate3d(${p.x}px, ${p.y}px, 0)`,
          transition: lifted ? 'none'
            : isDrag ? `transform ${DROP_MS}ms cubic-bezier(.2,.8,.25,1)`
            : animating ? `transform ${MOVE_MS}ms ${EASE}` : 'none',
          zIndex: isDrag ? 60 : 1,
          pointerEvents: lifted ? 'none' : undefined,
          willChange: drag ? 'transform' : undefined,
        }
        return (
          <div key={id} style={style} onPointerDown={e => H.current.down(e, id)}
            onContextMenu={e => { if (gesture.current) e.preventDefault() }}>
            <div ref={cardRef(id)} data-sort-id={id}
              className={'ib-sort-card' + (lifted ? ' is-lifted' : '') + (isDrag && drag.phase === 'drop' ? ' is-dropping' : '')}>
              {renderItem(byId[id])}
            </div>
          </div>
        )
      })}
    </div>
  )
}

export const SORT_CSS = `
.ib-sort { touch-action: manipulation; }
.ib-sort-card, .ib-sort-card img { -webkit-touch-callout: none; -webkit-user-select: none; user-select: none; }
.ib-sort-card { border-radius: 14px; transition: transform .22s cubic-bezier(.2,.7,.2,1), box-shadow .22s ease; transform-origin: 50% 40%; }
.ib-sort-card.is-lifted { transform: scale(1.045) rotate(-1.4deg); box-shadow: 0 28px 60px rgba(7,39,45,.30), 0 6px 16px rgba(7,39,45,.14); cursor: grabbing; }
.ib-sort-card.is-lifted .ib-pin, .ib-sort-card.is-lifted .ib-pin:hover { transform: none; box-shadow: none; }
.ib-sort-card.is-dropping { animation: ib-settle .42s cubic-bezier(.2,.8,.25,1) both; }
@keyframes ib-settle { 0% { transform: scale(1.045) rotate(-1.4deg); } 55% { transform: scale(.985); } 100% { transform: none; } }
.ib-sort-slot { position: absolute; left: 0; top: 0; border-radius: 14px; border: 2px dashed rgba(176,141,87,.6); background: rgba(176,141,87,.08);
  transition: transform .32s cubic-bezier(.2,.7,.2,1), height .2s; animation: ib-slot-in .2s ease both; pointer-events: none; }
@keyframes ib-slot-in { from { opacity: 0; } to { opacity: 1; } }
body.ib-dragging, body.ib-dragging * { cursor: grabbing !important; user-select: none !important; -webkit-user-select: none !important; }
@media (prefers-reduced-motion: reduce) {
  .ib-sort-card, .ib-sort-slot { transition: none !important; animation: none !important; }
}
`
